<template>
  <!-- Only for customers who are not billed through Stripe: everyone else renews
       in the Stripe portal and never needs to ask us for anything. -->
  <div v-if="canRequest" class="invoice-request-card">
    <div class="irc-head">
      <div class="irc-icon">
        <font-awesome-icon icon="fa-solid fa-file-invoice" />
      </div>
      <div class="irc-head-text">
        <h3>{{ $t('invoiceRequest.title') }}</h3>
        <p>{{ $t('invoiceRequest.subtitle') }}</p>
      </div>
    </div>

    <!-- An open request: show where it stands instead of the form. -->
    <div v-if="openRequest && !editing" class="irc-status" :class="`irc-status-${openRequest.status}`">
      <div class="irc-status-row">
        <span class="irc-status-badge">{{ statusLabel(openRequest.status) }}</span>
        <span class="irc-status-plan">
          {{ openRequest.plan_name || openRequest.plan_key }} ·
          {{ $t('invoiceRequest.months', { count: openRequest.period_months }) }} ·
          {{ formatAmount(openRequest.total_amount) }}
        </span>
      </div>
      <p class="irc-status-hint">{{ statusHint(openRequest.status) }}</p>
      <!-- Correcting a request is only possible before it becomes an invoice. -->
      <div v-if="openRequest.status === 'pending'" class="irc-status-actions">
        <button type="button" class="irc-link-btn" :disabled="working" @click="startEdit">
          {{ $t('invoiceRequest.edit') }}
        </button>
        <button type="button" class="irc-link-btn irc-link-danger" :disabled="working" @click="cancelRequest">
          {{ $t('invoiceRequest.cancel') }}
        </button>
      </div>
    </div>

    <!-- No open request, or correcting one that has not been invoiced yet. -->
    <template v-if="!openRequest || editing">
      <button v-if="!formOpen" type="button" class="irc-primary" @click="formOpen = true">
        <font-awesome-icon icon="fa-solid fa-plus" />
        {{ $t('invoiceRequest.request') }}
      </button>

      <!-- Condition stated outright rather than v-else: the hint below sits
           between the two and would otherwise capture the else branch. -->
      <form v-if="formOpen" class="irc-form" @submit.prevent="submit">
        <p v-if="editing" class="irc-editing-hint">{{ $t('invoiceRequest.editingHint') }}</p>

        <div class="irc-row">
          <label class="irc-field">
            <span>{{ $t('invoiceRequest.plan') }}</span>
            <select v-model="form.plan_key" required>
              <option v-for="plan in plans" :key="plan.key" :value="plan.key">
                {{ plan.name }} — {{ formatAmount(plan.monthly_amount) }}/{{ $t('invoiceRequest.month') }}
              </option>
            </select>
          </label>

          <label class="irc-field">
            <span>{{ $t('invoiceRequest.period') }}</span>
            <select v-model.number="form.period_months" required>
              <option v-for="months in periods" :key="months" :value="months">
                {{ $t('invoiceRequest.months', { count: months }) }}
                <template v-if="discountFor(months)"> — −{{ discountFor(months) }}%</template>
              </option>
            </select>
          </label>
        </div>

        <div class="irc-total">
          <!-- Broken down only when a discount applies; otherwise one line is
               clearer than a subtotal that equals the total. -->
          <template v-if="price.discount > 0">
            <div class="irc-total-row">
              <span>{{ $t('invoiceRequest.subtotal') }}</span>
              <span>{{ formatAmount(price.gross) }}</span>
            </div>
            <div class="irc-total-row irc-total-discount">
              <span>{{ $t('invoiceRequest.discount', { percent: price.percent }) }}</span>
              <span>−{{ formatAmount(price.discount) }}</span>
            </div>
          </template>
          <div class="irc-total-row irc-total-final">
            <span>{{ $t('invoiceRequest.total') }}</span>
            <strong>{{ formatAmount(price.total) }}</strong>
          </div>
        </div>

        <div class="irc-row">
          <label class="irc-field">
            <span>{{ $t('invoiceRequest.companyName') }} *</span>
            <input v-model="form.client_name" type="text" required maxlength="255" />
          </label>
          <label class="irc-field">
            <span>{{ $t('invoiceRequest.eik') }}</span>
            <input v-model="form.client_eik" type="text" maxlength="32" />
          </label>
        </div>

        <div class="irc-row">
          <label class="irc-field">
            <span>{{ $t('invoiceRequest.vatNumber') }}</span>
            <input v-model="form.client_vat_number" type="text" maxlength="32" />
          </label>
          <label class="irc-field">
            <span>{{ $t('invoiceRequest.mol') }}</span>
            <input v-model="form.client_mol" type="text" maxlength="255" />
          </label>
        </div>

        <label class="irc-field">
          <span>{{ $t('invoiceRequest.address') }}</span>
          <input v-model="form.client_address" type="text" maxlength="255" />
        </label>

        <label class="irc-field">
          <span>{{ $t('invoiceRequest.note') }}</span>
          <textarea v-model="form.note" rows="2" maxlength="1000"></textarea>
        </label>

        <p v-if="error" class="irc-error">{{ error }}</p>

        <div class="irc-actions">
          <button type="button" class="irc-secondary" :disabled="working" @click="closeForm">
            {{ $t('common.cancel') }}
          </button>
          <button type="submit" class="irc-primary" :disabled="working">
            {{ working
              ? $t('invoiceRequest.sending')
              : (editing ? $t('invoiceRequest.save') : $t('invoiceRequest.send')) }}
          </button>
        </div>
      </form>
    </template>

    <!-- Everything already settled, newest first. -->
    <div v-if="history.length" class="irc-history">
      <div class="irc-history-head">{{ $t('invoiceRequest.history') }}</div>
      <div v-for="item in history" :key="item.id" class="irc-history-row">
        <span class="irc-history-date">{{ formatDate(item.created_at) }}</span>
        <span class="irc-history-plan">
          {{ item.plan_name || item.plan_key }} · {{ $t('invoiceRequest.months', { count: item.period_months }) }}
        </span>
        <span class="irc-history-amount">{{ formatAmount(item.total_amount) }}</span>
        <span class="irc-history-status" :class="`is-${item.status}`">{{ statusLabel(item.status) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/utils/api';
import { useUiStore } from '@/stores/ui';

const { t } = useI18n();
const uiStore = useUiStore();

const canRequest = ref(false);
const plans = ref([]);
const periods = ref([1, 3, 6, 12]);
const currency = ref('EUR');
const openRequest = ref(null);
const history = ref([]);
const formOpen = ref(false);
const editing = ref(false);
const working = ref(false);
const error = ref('');

const form = ref({
  plan_key: '',
  period_months: 1,
  client_name: '',
  client_eik: '',
  client_vat_number: '',
  client_address: '',
  client_mol: '',
  client_email: '',
  note: '',
});

const periodDiscounts = ref({});

const discountFor = (months) => Number(periodDiscounts.value[months] || 0);

// Mirrors the server's priceFor(): the figure shown must match what gets stored.
const price = computed(() => {
  const plan = plans.value.find(p => p.key === form.value.plan_key);
  const months = form.value.period_months;

  if (!plan) return { gross: 0, percent: 0, discount: 0, total: 0 };

  const gross = round2(plan.monthly_amount * months);
  const percent = discountFor(months);
  const discount = round2(gross * percent / 100);

  return { gross, percent, discount, total: round2(gross - discount) };
});

const round2 = (value) => Math.round(value * 100) / 100;

const formatAmount = (value) =>
  `${Number(value || 0).toFixed(2)} ${currency.value}`;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '');

const statusLabel = (status) => t(`invoiceRequest.status.${status}`);
const statusHint = (status) => t(`invoiceRequest.hint.${status}`);

const load = async () => {
  try {
    const { data } = await api.get('/api/plan-invoice-requests/options');

    canRequest.value = data.can_request;
    plans.value = data.plans || [];
    periods.value = data.periods || periods.value;
    periodDiscounts.value = data.period_discounts || {};
    currency.value = data.currency || 'EUR';
    openRequest.value = data.open_request;

    // Prefill from the customer's own company so they confirm rather than type.
    form.value = { ...form.value, ...(data.billing || {}) };
    if (!form.value.plan_key && plans.value.length) {
      form.value.plan_key = plans.value[0].key;
    }

    if (canRequest.value) {
      const list = await api.get('/api/plan-invoice-requests');
      history.value = (list.data.data || []).filter(r => !['pending', 'invoiced'].includes(r.status));
    }
  } catch (e) {
    // A failure here only means the card stays hidden; the page still works.
    console.error('Failed to load invoice request options:', e);
  }
};

// Reopens the form on the request as submitted, so the customer corrects what is
// there rather than retyping it.
const startEdit = () => {
  const r = openRequest.value;

  form.value = {
    plan_key: r.plan_key,
    period_months: r.period_months,
    client_name: r.client_name || '',
    client_eik: r.client_eik || '',
    client_vat_number: r.client_vat_number || '',
    client_address: r.client_address || '',
    client_mol: r.client_mol || '',
    client_email: r.client_email || '',
    note: r.note || '',
  };

  editing.value = true;
  formOpen.value = true;
  error.value = '';
};

const closeForm = () => {
  formOpen.value = false;
  editing.value = false;
  error.value = '';
};

const submit = async () => {
  working.value = true;
  error.value = '';

  // Captured before closeForm() clears the flag.
  const isEdit = editing.value;

  try {
    const { data } = isEdit
      ? await api.put(`/api/plan-invoice-requests/${openRequest.value.id}`, form.value)
      : await api.post('/api/plan-invoice-requests', form.value);

    openRequest.value = data.data;
    closeForm();
    uiStore.showToast({
      message: isEdit ? t('invoiceRequest.updated') : t('invoiceRequest.sent'),
      type: 'success',
    });
  } catch (e) {
    error.value = e.response?.data?.message || t('invoiceRequest.failed');
  } finally {
    working.value = false;
  }
};

const cancelRequest = async () => {
  working.value = true;

  try {
    await api.post(`/api/plan-invoice-requests/${openRequest.value.id}/cancel`);
    openRequest.value = null;
    await load();
    uiStore.showToast({ message: t('invoiceRequest.cancelled'), type: 'success' });
  } catch (e) {
    error.value = e.response?.data?.message || t('invoiceRequest.failed');
  } finally {
    working.value = false;
  }
};

onMounted(load);
</script>

<style scoped>
/* Matches .current-plan-card on the page so the two sit in the same column
   rather than this one running the full width of the container. */
.invoice-request-card {
  width: 100%;
  max-width: 1004px;
  margin: 0 auto 40px;
  background: #fff;
  border: 1px solid rgba(31, 41, 51, 0.08);
  border-radius: 24px;
  padding: 28px 34px;
  box-shadow: 0 26px 60px -40px rgba(31, 41, 51, 0.3);
}

.irc-head { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 18px; }

.irc-icon {
  width: 42px; height: 42px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 12px; color: #fff; font-size: 17px;
  background: linear-gradient(150deg, #5aa2ff, #2b82f0);
}

.irc-head-text h3 { margin: 0 0 4px; font-size: 17px; font-weight: 700; color: #111827; }
.irc-head-text p { margin: 0; font-size: 13.5px; color: #6b7280; line-height: 1.5; }

.irc-primary,
.irc-secondary {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 10px 20px; border-radius: 10px; border: 1px solid transparent;
  font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s ease;
}
.irc-primary { background: #2b82f0; color: #fff; }
.irc-primary:hover:not(:disabled) { background: #1a6fdc; }
.irc-secondary { background: #fff; color: #374151; border-color: #e5e7eb; }
.irc-secondary:hover:not(:disabled) { border-color: #9ca3af; }
.irc-primary:disabled, .irc-secondary:disabled { opacity: 0.6; cursor: default; }

.irc-form { display: flex; flex-direction: column; gap: 14px; }
.irc-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

.irc-field { display: flex; flex-direction: column; gap: 6px; }
.irc-field > span { font-size: 12.5px; font-weight: 600; color: #374151; }
.irc-field input,
.irc-field select,
.irc-field textarea {
  width: 100%; padding: 10px 12px; border: 1px solid #e5e7eb; border-radius: 9px;
  font-size: 14px; font-family: inherit; color: #111827; background: #fff;
}
.irc-field input:focus,
.irc-field select:focus,
.irc-field textarea:focus {
  outline: none; border-color: #2b82f0; box-shadow: 0 0 0 3px rgba(43, 130, 240, 0.12);
}

.irc-total {
  padding: 12px 16px; border-radius: 10px;
  background: rgba(43, 130, 240, 0.06); border: 1px solid rgba(43, 130, 240, 0.16);
  font-size: 14px; color: #374151;
}
.irc-total-row { display: flex; align-items: center; justify-content: space-between; padding: 2px 0; }
.irc-total-discount { color: #059669; font-weight: 600; }
.irc-total-final { padding-top: 8px; }
.irc-total-row + .irc-total-final {
  margin-top: 6px; border-top: 1px solid rgba(43, 130, 240, 0.18);
}
.irc-total strong { font-size: 18px; color: #1d4ed8; }

.irc-actions { display: flex; justify-content: flex-end; gap: 10px; }

.irc-error {
  margin: 0; padding: 10px 14px; border-radius: 9px;
  background: #fef2f2; color: #b91c1c; font-size: 13px;
  border: 1px solid #fecaca;
}

.irc-status { padding: 16px; border-radius: 12px; border: 1px solid #e5e7eb; background: #f9fafb; }
.irc-status-pending { background: #fffbeb; border-color: #fde68a; }
.irc-status-invoiced { background: #eff6ff; border-color: #bfdbfe; }

.irc-status-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.irc-status-badge {
  padding: 3px 10px; border-radius: 999px; background: #fff;
  border: 1px solid #e5e7eb; font-size: 12px; font-weight: 700; color: #374151;
}
.irc-status-plan { font-size: 14px; font-weight: 600; color: #111827; }
.irc-status-hint { margin: 8px 0 0; font-size: 13px; color: #6b7280; line-height: 1.5; }

.irc-status-actions { display: flex; align-items: center; gap: 16px; margin-top: 10px; }

.irc-link-btn {
  padding: 0; border: 0; background: none;
  color: #2b82f0; font-size: 13px; font-weight: 600; cursor: pointer; text-decoration: underline;
}
.irc-link-btn:hover:not(:disabled) { color: #1a6fdc; }
.irc-link-btn:disabled { opacity: 0.5; cursor: default; }

.irc-link-danger { color: #6b7280; }
.irc-link-danger:hover:not(:disabled) { color: #b91c1c; }

.irc-editing-hint {
  margin: 0 0 4px; padding: 9px 13px; border-radius: 9px;
  background: #fffbeb; border: 1px solid #fde68a;
  color: #92400e; font-size: 13px;
}

/* Set on its own tinted panel: sitting flush under the form, these settled
   requests read as part of it, and their amounts get mistaken for the total. */
.irc-history {
  margin-top: 24px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
}
.irc-history-head {
  font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: #9ca3af; margin-bottom: 8px;
}
.irc-history-row {
  display: grid; grid-template-columns: 90px 1fr auto auto; gap: 12px;
  align-items: center; padding: 7px 0; font-size: 13px; color: #6b7280;
}
.irc-history-row + .irc-history-row { border-top: 1px solid #f3f4f6; }
.irc-history-date { color: #9ca3af; }
.irc-history-plan { color: #374151; }
.irc-history-amount { font-weight: 600; color: #111827; }
.irc-history-status { font-size: 12px; font-weight: 600; }
.irc-history-status.is-paid { color: #059669; }
.irc-history-status.is-cancelled { color: #9ca3af; }

@media (max-width: 640px) {
  .irc-row { grid-template-columns: 1fr; }
  .irc-history-row { grid-template-columns: 1fr auto; row-gap: 2px; }
  .irc-history-date { grid-column: 1 / -1; }
}
</style>
